import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoneyPrecise } from '../../utils/money';

/**
 * TREATX-PLAN-1 — renders THE accepted payment plan, exactly as derived.
 *
 * Presentation only: every amount comes from the backend
 * `financial_summary.collections` list (one derivation, shared with the offer
 * letter, the PDF and the payment obligations). This component performs no
 * money arithmetic — it must never divide a balance or round an amount, which
 * is exactly how "3 × 300.01" came to be shown for a 300.00/300.00/300.02 plan.
 */
export const PaymentPlanSchedule = ({ summary, currency, depositType, depositValue, testId = 'payment-plan' }) => {
  const { t } = useTranslation();
  const fs = summary || {};
  const ccy = fs.currency || currency || '';
  const collections = Array.isArray(fs.collections) ? fs.collections : [];

  if (!collections.length) {
    // No derivable plan (legacy or contradictory terms). Say so — never
    // substitute a guessed schedule.
    return (
      <p className="text-sm text-gray-500" data-testid={`${testId}-unavailable`}>
        {t('payment.plan.unavailable', 'The payment schedule is not available for this offer.')}
      </p>
    );
  }

  const label = (c) => {
    if (c.collection_type === 'deposit') {
      const pct = depositType === 'percentage' && depositValue != null ? ` (${depositValue}%)` : '';
      return `${t('payment.plan.deposit', 'Deposit')}${pct}`;
    }
    if (c.collection_type === 'installment') {
      return t('payment.plan.installmentOf', {
        number: c.installment_number,
        count: c.installment_count,
        defaultValue: `Installment ${c.installment_number} of ${c.installment_count}`,
      });
    }
    if (c.collection_type === 'balance') return t('payment.plan.balance', 'Balance');
    return t('payment.plan.fullPayment', 'Full payment');
  };

  return (
    <div data-testid={testId}>
      <ol className="text-sm text-gray-700 space-y-1">
        {collections.map((c) => (
          <li key={c.sequence} className="flex justify-between gap-4" data-testid={`${testId}-row-${c.sequence}`}>
            <span>{label(c)}</span>
            <span className="font-medium" data-testid={`${testId}-amount-${c.sequence}`}>
              {formatMoneyPrecise(c.amount, ccy)}
            </span>
          </li>
        ))}
      </ol>
      <p className="text-xs text-gray-500 mt-1" data-testid={`${testId}-count`}>
        {t('payment.plan.collectionCount', {
          count: fs.collection_count || collections.length,
          defaultValue: `{{count}} separate payments`,
        })}
      </p>
      {/* TRACK-B-01 — TIMING IS NOT PART OF THIS OFFER.
          The accepted schedule (`quote_financials.derive_payment_plan`) carries
          sequence, type and amount only; no due date, interval or trigger field
          exists anywhere in the offer → contract → obligation chain. The
          successor obligation is activated when its predecessor is paid
          (`PaymentObligationService._ensure_successor_activated`), which is an
          ACTIVATION, not an agreed due date. Stating that plainly keeps the
          contract truthful without inventing a timetable or changing any
          collection right. */}
      <p className="text-xs text-gray-500 mt-1" data-testid={`${testId}-timing-basis`}>
        {t('payment.plan.timingBasis',
          'No calendar due dates are part of this offer. Each payment becomes '
          + 'available once the previous one is completed.')}
      </p>
    </div>
  );
};

export default PaymentPlanSchedule;
